import { foldCharacter } from './search-text';

/** A name split around the matched substring, so the match can be marked up without innerHTML. */
export interface HighlightedName {
  before: string;
  match: string;
  after: string;
}

/**
 * `query` is expected from `toSearchQuery`. The match is found ignoring case and accents, and marked
 * in the name as written, so "lefevre" marks "Lefèvre".
 */
export function highlight(name: string, query: string): HighlightedName {
  if (!query) {
    return { before: name, match: '', after: '' };
  }

  // Folding can change length (æ → ae), so each folded unit remembers the original character it came from.
  let folded = '';
  const sourceStart: number[] = [];
  const sourceEnd: number[] = [];
  let offset = 0;
  for (const character of name) {
    const foldedCharacter = foldCharacter(character);
    for (let unit = 0; unit < foldedCharacter.length; unit++) {
      sourceStart.push(offset);
      sourceEnd.push(offset + character.length);
    }
    folded += foldedCharacter;
    offset += character.length;
  }

  const index = folded.indexOf(query);
  if (index < 0) {
    return { before: name, match: '', after: '' };
  }

  const start = sourceStart[index];
  let end = sourceEnd[index + query.length - 1];
  // A decomposed accent folds to nothing; it still belongs to the letter it sits on.
  while (end < name.length) {
    const next = String.fromCodePoint(name.codePointAt(end) ?? 0);
    if (foldCharacter(next)) {
      break;
    }
    end += next.length;
  }

  return {
    before: name.slice(0, start),
    match: name.slice(start, end),
    after: name.slice(end),
  };
}
